import { Button } from '@askable/ui/core/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@askable/ui/core/dialog';
import { toast } from '@askable/ui/core/sonner';
import { Textarea } from '@askable/ui/core/textarea';
import _ from 'lodash';
import { useState, Fragment } from 'react';
import { useMutation } from 'urql';

import { bookingUtils } from 'src/lib/booking';

import { LoadingOverlay } from 'components/common';
import { ReportIssue } from 'containers/Booking/containers/BookingParticipantsContainer/components/BookingParticipantsSidePanel/queries/ReportIssue.mutation';
import { ReportReason, ReportRequest } from 'src/constants/graphql-enums';

import './participantIssueDialog.scss';

function ParticipantIssueDialog(props: any) {
  const [loading, setLoading] = useState(false);
  const [issueMessage, setIssueMessage] = useState('');
  const [issueRequest, setIssueRequest] = useState(null);

  const [, reportIssue] = useMutation(ReportIssue);

  const onCloseModal = () => {
    setLoading(false);
    setIssueRequest(null);
    setIssueMessage('');
    props.onClose();
  };

  const refundCredits = (booking: any) => {
    // round the number of credits up to the nearest whole number
    const amount = Math.ceil(_.get(booking, 'total_amount.creditsPerParticipant', 1));
    return amount === 1 ? '1 credit' : `${amount} credits`;
  };

  const renderConfirmationMessage = () => {
    return (
      <Fragment>
        <div className="actionMessage">
          {issueRequest === 1 && (
            <p>
              <strong>{refundCredits(props.booking)}</strong> will be refunded back into your team credit balance.
            </p>
          )}
          {issueRequest === 2 && (
            <p>
              An <strong>additional slot</strong> will be opened up for you to invite a another person.
            </p>
          )}
        </div>
        <p className="messageLabel">
          <strong>Please</strong> take a second to give us some feedback on why there was an issue with{' '}
          {_.get(props.participant, 'user.meta.identity.firstname')}{' '}
          {_.get(props.participant, 'user.meta.identity.lastname')}:
        </p>
        <Textarea id="issueMessage" onChange={(e: any) => setIssueMessage(e.target.value)} rows={8} />
        <Button
          onClick={() => {
            if (issueRequest) {
              setLoading(true);

              void reportIssue({
                bookingId: props.booking._id,
                bookingSubmissionId: props.participant.Submission?._id,
                reportReason: ReportReason.Others,
                reportRequest: issueRequest === 1 ? ReportRequest.Refund : ReportRequest.Replace,
                reportDetails: issueMessage.trim(),
              }).then(result => {
                setLoading(false);
                if (result.error) {
                  toast.error('Something went wrong. Please try again.');
                  return;
                }
                if (props.onConfirmIssue) props.onConfirmIssue();
                props.onClickDone();
              });
            }
          }}
          className="doneButton"
          disabled={!issueMessage.trim()}
          size="default"
        >
          Submit Feedback
        </Button>
      </Fragment>
    );
  };

  return (
    <Dialog open={props.open} onOpenChange={open => !open && onCloseModal()}>
      <DialogContent className="participantIssueDialogComponent" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="sr-only">Report an issue with this participant</DialogTitle>
        </DialogHeader>
        <div className="content text-pretty text-sm text-muted-foreground">
          {loading && <LoadingOverlay style={{ opacity: 0.8 }} />}
          {!issueRequest ? (
            <Fragment>
              <div className="topContainer">
                <div className="contentTop">
                  <h2>
                    We're sorry to hear that there was an issue with{' '}
                    {_.get(props.participant, 'user.meta.identity.firstname')}. You can{' '}
                    {!bookingUtils.isBYO(props.booking)
                      ? 'choose one of the options below:'
                      : 'replace this participant.'}
                  </h2>
                </div>
              </div>
              <div className="actionContainer">
                <div className="replacement">
                  <div className="internalContent">
                    <h3>Replace</h3>
                    <p>
                      We'll open up a spot for you on this study and you&apos;ll be able to invite a different person,
                      free of charge. If {_.get(props.participant, 'user.meta.identity.firstname')} was your last
                      session, you may need to create some additional timeslots before inviting a replacement.
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '2' is not assignable to paramete... Remove this comment to see the full error message
                    onClick={() => setIssueRequest(2)}
                    className="mb-5 mt-2 max-w-[150px]"
                  >
                    Replace
                  </Button>
                </div>
                {!bookingUtils.isBYO(props.booking) && (
                  <div className="refund">
                    <div className="internalContent">
                      <h3>Credit refund</h3>
                      <p>
                        We&apos;ll immediately put the credits back into your team&apos;s credit balance, which
                        you&apos;ll be able to use for a future study.
                      </p>
                    </div>
                    <Button
                      variant="primary"
                      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '1' is not assignable to paramete... Remove this comment to see the full error message
                      onClick={() => setIssueRequest(1)}
                      className="mb-5 mt-2 max-w-[150px]"
                    >
                      Refund
                    </Button>
                  </div>
                )}
              </div>
            </Fragment>
          ) : (
            <div className="confirmationMessageContainer">{renderConfirmationMessage()}</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ParticipantIssueDialog;
